import { v2Api } from './v2Client';
/** Type d'une réservation (type de match, `MatchCategory`) : de quoi afficher un badge et colorer le planning. */
function normalizeCategorySummary(row) {
  if (!row) return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id: String(id),
    name: row.name ?? row.Name ?? '',
    color_hex: row.color_hex ?? row.colorHex ?? row.ColorHex ?? null,
    icon: row.icon ?? row.Icon ?? null,
  };
}

const LOG = '[SquashAPI terrainReservation]';

/** Un participant (`TerrainReservationPlayerDto`) — membre inscrit (player_id) ou invité (guest_name). */
function normalizePlayerEntry(p) {
  if (!p) return null;
  return {
    player_id: p.player_id ?? p.playerId ?? p.PlayerId ?? null,
    player_name: p.player_name ?? p.playerName ?? p.PlayerName ?? null,
    player_picture: p.player_picture ?? p.playerPicture ?? p.PlayerPicture ?? null,
    guest_name: p.guest_name ?? p.guestName ?? p.GuestName ?? null,
    phone: p.phone ?? p.Phone ?? null,
    is_responsible: Boolean(p.is_responsible ?? p.isResponsible ?? p.IsResponsible ?? false),
  };
}

/** Une invitation "on joue" (mobile) en attente (`TerrainReservationInvitedPlayerDto`) — voir
 * ReservationPlayersTable, affichée en lecture seule sous les joueurs confirmés. */
function normalizePendingInvite(p) {
  if (!p) return null;
  return {
    player_id: p.player_id ?? p.playerId ?? p.PlayerId ?? null,
    player_name: p.player_name ?? p.playerName ?? p.PlayerName ?? null,
    player_picture: p.player_picture ?? p.playerPicture ?? p.PlayerPicture ?? null,
    invited_at: p.invited_at ?? p.invitedAt ?? p.InvitedAt ?? null,
  };
}

/** Résumé de la `TerrainReservationSeries` (règle) dont provient une occurrence — voir
 * TerrainReservationSeriesSummaryDto. Absent (null) pour un ancien groupe ad-hoc sans règle associée. */
function normalizeSeriesSummary(s) {
  if (!s) return null;
  return {
    id: s.id ?? s.Id,
    day_of_week: s.day_of_week ?? s.dayOfWeek ?? s.DayOfWeek ?? null,
    start_time: s.start_time ?? s.startTime ?? s.StartTime ?? '',
    end_time: s.end_time ?? s.endTime ?? s.EndTime ?? '',
    start_date: s.start_date ?? s.startDate ?? s.StartDate ?? null,
    end_date: s.end_date ?? s.endDate ?? s.EndDate ?? null,
    is_indefinite: Boolean(s.is_indefinite ?? s.isIndefinite ?? s.IsIndefinite ?? false),
    // Règle (Services/TerrainRecurrence.cs) : séries antérieures = hebdomadaire sur day_of_week.
    frequency: s.frequency ?? s.Frequency ?? 'weekly',
    interval: Number(s.interval ?? s.Interval ?? 1) || 1,
    days_of_week: Array.isArray(s.days_of_week ?? s.daysOfWeek)
      ? (s.days_of_week ?? s.daysOfWeek)
      : s.day_of_week != null
        ? [s.day_of_week]
        : [],
    day_of_month: s.day_of_month ?? s.dayOfMonth ?? null,
    occurrence_count: s.occurrence_count ?? s.occurrenceCount ?? null,
    last_date: s.last_date ?? s.lastDate ?? null,
  };
}

/**
 * Normalise un bloc du planning à partir de `TerrainOccupancyDto` (voir Controllers/DTOs backend) —
 * une réservation (booking/blocked) OU une fermeture exceptionnelle (kind "closed", pas de
 * reservation_id — id synthétique construit à partir du terrain/de l'horaire, jamais éditable via
 * ReservationDialog). Tolère camelCase/PascalCase en repli, comme les autres services.
 */
function normalizeReservationBlock(row) {
  if (!row) return null;
  const terrainId = row.terrain_id ?? row.terrainId ?? row.TerrainId ?? null;
  const startAt = row.start_at ?? row.startAt ?? row.StartAt ?? null;
  const kind = row.kind ?? row.Kind ?? 'booking'; // 'booking' | 'blocked' | 'closed'
  const id = row.reservation_id ?? row.id ?? row.reservationId ?? row.Id ?? (kind === 'closed' ? `closure-${terrainId}-${startAt}` : null);
  if (id == null) return null;
  const playersRaw = row.players ?? row.Players ?? [];
  const pendingInvitesRaw = row.pending_invites ?? row.pendingInvites ?? row.PendingInvites ?? [];
  return {
    id: String(id),
    terrain_id: terrainId,
    terrain_number: row.terrain_number ?? row.terrainNumber ?? row.TerrainNumber ?? null,
    start_at: startAt,
    end_at: row.end_at ?? row.endAt ?? row.EndAt ?? null,
    kind,
    status: row.status ?? row.Status ?? 'confirmed',
    players: Array.isArray(playersRaw) ? playersRaw.map(normalizePlayerEntry).filter(Boolean) : [],
    pending_invites: Array.isArray(pendingInvitesRaw) ? pendingInvitesRaw.map(normalizePendingInvite).filter(Boolean) : [],
    guest_phone: row.guest_phone ?? row.guestPhone ?? row.GuestPhone ?? null,
    label: row.label ?? row.Label ?? null,
    category: normalizeCategorySummary(row.category ?? row.Category ?? null),
    price_cents_computed: row.price_cents_computed ?? row.priceCentsComputed ?? null,
    price_cents_override: row.price_cents_override ?? row.priceCentsOverride ?? null,
    payment_status: row.payment_status ?? row.paymentStatus ?? 'not_required',
    payment_provider: row.payment_provider ?? row.paymentProvider ?? null,
    paid_amount_cents: row.paid_amount_cents ?? row.paidAmountCents ?? null,
    refunded_amount_cents: row.refunded_amount_cents ?? row.refundedAmountCents ?? null,
    wallet_paid_cents: Number(row.wallet_paid_cents ?? row.walletPaidCents ?? 0) || 0,
    wallet_refunded_cents: Number(row.wallet_refunded_cents ?? row.walletRefundedCents ?? 0) || 0,
    payment_transaction_id: row.payment_transaction_id ?? row.paymentTransactionId ?? null,
    recurrence_group_id: row.recurrence_group_id ?? row.recurrenceGroupId ?? row.RecurrenceGroupId ?? null,
    series: normalizeSeriesSummary(row.series ?? row.Series ?? null),
    row_version: row.row_version ?? row.rowVersion ?? row.RowVersion ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
  };
}

/**
 * GET /TerrainReservation/planning?date&terrain_id — réservations confirmées pour la journée.
 * `terrainId` omis = tous les terrains.
 * @param {string} dateIso date du jour affiché (YYYY-MM-DD)
 * @param {string} [terrainId]
 */
export async function fetchTerrainPlanning(dateIso, terrainId) {
  try {
    const params = { date: dateIso };
    if (terrainId) params.terrain_id = terrainId;
    const { data } = await v2Api.get('/TerrainReservation/planning', { params });
    // GET /TerrainReservation/planning renvoie TerrainPlanningDto = { date, occupancy: [...] }.
    const rows = Array.isArray(data) ? data : (data?.occupancy ?? data?.blocks ?? data?.reservations ?? []);
    return (Array.isArray(rows) ? rows : []).map(normalizeReservationBlock).filter(Boolean);
  } catch (err) {
    console.error(LOG, 'planning échoué', { dateIso, terrainId, err });
    throw err;
  }
}

/**
 * GET /TerrainReservation/planning_range?start&end&terrain_id — comme `fetchTerrainPlanning` mais sur
 * une plage de jours (bornes incluses) en un seul aller-retour, pour éviter un appel par jour (utilisé
 * par la vue Liste de l'ERP, fenêtre réglable jusqu'à plusieurs mois).
 * @param {string} startIso @param {string} endIso @param {string} [terrainId]
 */
export async function fetchTerrainPlanningRange(startIso, endIso, terrainId) {
  try {
    const params = { start: startIso, end: endIso };
    if (terrainId) params.terrain_id = terrainId;
    const { data } = await v2Api.get('/TerrainReservation/planning_range', { params });
    const rows = Array.isArray(data) ? data : data?.occupancy ?? [];
    return (Array.isArray(rows) ? rows : []).map(normalizeReservationBlock).filter(Boolean);
  } catch (err) {
    console.error(LOG, 'planning_range échoué', { startIso, endIso, terrainId, err });
    throw err;
  }
}

/**
 * POST /TerrainReservation/create — création simple, ou récurrente si `recurrence_until` /
 * `recurrence_weeks` / `recurrence_indefinite` est renseigné (même jour/heure locale que `start_at`,
 * chaque semaine — voir TerrainReservationSeries, les occurrences au-delà de l'horizon glissant sont
 * complétées plus tard par le job périodique, pas toutes créées d'un coup). Peut échouer en 409 :
 *  - `{ error: 'terrain_conflict', conflicts: [...] }` (chevauchement avec une autre réservation)
 *  - `{ error: 'outside_opening_hours' }` (hors horaires — relancer avec `override_hours: true`)
 */
export async function createTerrainReservation(payload) {
  try {
    const { data } = await v2Api.post('/TerrainReservation/create', payload);
    return data;
  } catch (err) {
    console.error(LOG, 'create échoué', { payload, err });
    throw err;
  }
}

/**
 * POST /TerrainReservation/update — édition d'une occurrence ou "cette occurrence et les suivantes"
 * (`scope: 'occurrence' | 'this_and_future'`). Envoie `row_version` pour la concurrence optimiste ;
 * 409 `{ error: 'stale_version' }` si modifiée entretemps.
 */
export async function updateTerrainReservation(payload) {
  try {
    const { data } = await v2Api.post('/TerrainReservation/update', payload);
    return data;
  } catch (err) {
    console.error(LOG, 'update échoué', { payload, err });
    throw err;
  }
}

/**
 * POST /TerrainReservation/cancel
 * @param {string} id
 * @param {'occurrence'|'this_and_future'} [scope]
 * @param {string} [reason]
 * @param {string|number} [rowVersion] concurrence optimiste (voir `updateTerrainReservation`)
 * @param {boolean} [refundPayment] réservation(s) payée(s) en ligne : rembourser (défaut API : oui) ou non
 */
export async function cancelTerrainReservation(id, scope = 'this', reason, rowVersion, refundPayment) {
  try {
    const payload = { id: String(id), scope };
    if (reason) payload.cancellation_reason = reason;
    if (rowVersion != null) payload.row_version = rowVersion;
    if (typeof refundPayment === 'boolean') payload.refund_payment = refundPayment;
    const { data } = await v2Api.post('/TerrainReservation/cancel', payload);
    return data;
  } catch (err) {
    console.error(LOG, 'cancel échoué', { id, scope, err });
    throw err;
  }
}

/**
 * POST /Payment/refund — rembourse une réservation payée en ligne, intégralement (amountCents absent) ou
 * partiellement (voir Services/Payments côté API ; détail et historique : payments.service.js).
 * @param {string} reservationId
 * @param {{ amountCents?: number, reason?: string }} [options]
 */
export async function refundTerrainReservationPayment(reservationId, { amountCents, reason } = {}) {
  try {
    const payload = { reservation_id: String(reservationId) };
    if (amountCents != null) payload.amount_cents = amountCents;
    if (reason) payload.reason = reason;
    const { data } = await v2Api.post('/Payment/refund', payload);
    return data;
  } catch (err) {
    console.error(LOG, 'refund échoué', { reservationId, err });
    throw err;
  }
}

/**
 * GET /TerrainReservation/player/{id}?status=upcoming|past|canceled — réservations d'un joueur (fiche joueur ERP).
 * `upcoming` est chronologique, `past` / `canceled` du plus récent au plus ancien.
 */
export async function fetchPlayerReservations(playerId, { status = 'upcoming', page = 1, pageSize = 20 } = {}) {
  const params = { status, page, page_size: pageSize };
  try {
    const { data } = await v2Api.get(`/TerrainReservation/player/${encodeURIComponent(playerId)}`, { params });
    const items = (Array.isArray(data?.items) ? data.items : [])
      .map((row) => {
        const base = normalizeReservationBlock(row);
        if (!base) return null;
        return {
          ...base,
          terrain_name: row.terrain_name ?? null,
          terrain_sponsor: row.terrain_sponsor ?? null,
          price_cents_final: row.price_cents_final ?? null,
          cancelled_at: row.cancelled_at ?? null,
          cancellation_reason: row.cancellation_reason ?? null,
        };
      })
      .filter(Boolean);
    return { items, total: data?.total ?? 0, hasMore: Boolean(data?.has_more) };
  } catch (err) {
    console.error(LOG, 'player échoué', { playerId, params, err });
    throw err;
  }
}

/** Aperçu du prix (même calcul que l'enregistrement) : prix partagé, chaque joueur à son tarif (formule s'il est
 * membre), `playerIds` = joueurs de la réservation (null = invité). */
export async function previewTerrainReservationPrice({ terrainId, startAt, endAt, playerIds, typeId }) {
  const { data } = await v2Api.post('/TerrainReservation/price_preview', {
    terrain_id: terrainId,
    start_at: startAt,
    end_at: endAt,
    player_ids: Array.isArray(playerIds) ? playerIds : [],
    // Type de la réservation : tarifs de ce type (ex. « Cours »).
    type_id: typeId || null,
  });
  return {
    totalCents: data?.total_cents ?? null,
    memberPriceApplied: Boolean(data?.member_price_applied),
    segments: (Array.isArray(data?.segments) ? data.segments : []).map((s) => ({
      startAt: s.start_at,
      endAt: s.end_at,
      ruleId: s.rule_id ?? null,
      ruleLabel: s.rule_label ?? null,
      pricePerHourCents: s.price_cents_per_hour ?? null,
      priceCents: s.price_cents ?? null,
      membershipPlanName: s.membership_plan_name ?? null,
      terrainSpecific: Boolean(s.terrain_specific),
      shares: (Array.isArray(s.shares) ? s.shares : []).map((sh) => ({
        seat: Number(sh.seat ?? 0),
        playerId: sh.player_id ?? null,
        ruleId: sh.rule_id ?? null,
        ruleLabel: sh.rule_label ?? null,
        pricePerHourCents: sh.price_cents_per_hour ?? null,
        priceCents: sh.price_cents ?? null,
        membershipPlanName: sh.membership_plan_name ?? null,
        discountPercent: Number(sh.discount_percent ?? 0) || 0,
        // Tarif d'un type de réservation (ex. « Cours ») : son nom.
        typeName: sh.match_category_name || null,
        basePricePerHourCents: sh.base_price_cents_per_hour ?? null,
      })),
    })),
  };
}

/** Matchs de box non joués (saison en cours, sans partie) qu'une réservation peut programmer ; `q` = nom d'un joueur
 * ou du box, `playerIds` = joueurs déjà sur la réservation (seulement les matchs où ils jouent tous). */
export async function fetchBookableBoxMatches(q, playerIds) {
  const params = {};
  if (q) params.q = q;
  const ids = (playerIds || []).filter(Boolean);
  if (ids.length) params.players = ids.join(',');
  const { data } = await v2Api.get('/TerrainReservation/box_matches', { params });
  const player = (p) => ({
    id: p?.id ?? null,
    name: `${p?.first_name ?? ''} ${p?.last_name ?? ''}`.trim(),
    picture: p?.picture ?? '',
  });
  return (Array.isArray(data) ? data : []).map((m) => ({
    id: m.match_id,
    boxName: m.box_name ?? '',
    boxLevel: m.box_level ?? null,
    weekNumber: m.week_number ?? null,
    seasonName: m.season_name ?? '',
    competitionName: m.competition_name ?? '',
    scheduledAt: m.scheduled_at ?? null,
    postponed: m.delayed_status === 'confirmed',
    playerA: player(m.player_a),
    playerB: player(m.player_b),
  }));
}

export const terrainReservationService = {
  pricePreview: previewTerrainReservationPrice,
  boxMatches: fetchBookableBoxMatches,
  listForPlayer: fetchPlayerReservations,
  planning: fetchTerrainPlanning,
  planningRange: fetchTerrainPlanningRange,
  create: createTerrainReservation,
  update: updateTerrainReservation,
  cancel: cancelTerrainReservation,
  refundPayment: refundTerrainReservationPayment,
  normalizeBlock: normalizeReservationBlock,
};
